import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { ScanLine, Save, TestTube, Loader2, Check, X, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OCRSettings = () => {
  const [config, setConfig] = useState({
    provider: 'disabled', apiKey: '', apiKeyMasked: '', model: '',
    monthlyQuota: 50, alertThreshold: 80,
    enabledDocTypes: ['carte_grise', 'permis_conduire', 'piece_identite']
  });
  const [usage, setUsage] = useState({ total: 0, quota: 50, percentUsed: 0, byType: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [newKey, setNewKey] = useState('');

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const h = { Authorization: 'Bearer cookie-auth' };
      const [cfgRes, usageRes] = await Promise.all([
        axios.get(`${API}/ocr/config`, { headers: h }),
        axios.get(`${API}/ocr/usage`, { headers: h })
      ]);
      setConfig(cfgRes.data);
      setUsage(usageRes.data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        provider: config.provider,
        model: config.model || undefined,
        monthlyQuota: parseInt(config.monthlyQuota) || 50,
        alertThreshold: parseInt(config.alertThreshold) || 80,
        enabledDocTypes: config.enabledDocTypes,
      };
      if (newKey) payload.apiKey = newKey;
      await axios.post(`${API}/ocr/config`, payload, {});
      toast({ title: 'Configuration OCR sauvegardee' });
      setNewKey('');
      fetchAll();
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Erreur', variant: 'destructive' });
    }
    setSaving(false);
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const payload = { provider: config.provider, model: config.model };
      if (newKey) payload.apiKey = newKey;
      const res = await axios.post(`${API}/ocr/config/test`, payload, {});
      setTestResult(res.data);
    } catch (e) {
      setTestResult({ success: false, message: e.response?.data?.detail || 'Erreur de test' });
    }
    setTesting(false);
  };

  const toggleDocType = (docType) => {
    setConfig(prev => {
      const types = prev.enabledDocTypes.includes(docType)
        ? prev.enabledDocTypes.filter(t => t !== docType)
        : [...prev.enabledDocTypes, docType];
      return { ...prev, enabledDocTypes: types };
    });
  };

  const providerModels = {
    openai: [{ value: 'gpt-4o', label: 'GPT-4o' }, { value: 'gpt-4o-mini', label: 'GPT-4o Mini' }],
    gemini: [{ value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash' }, { value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro' }]
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  const usagePercent = usage.percentUsed || 0;

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Provider & Key */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <ScanLine className="w-4 h-4 text-amber-600" />
            Fournisseur OCR
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Fournisseur</Label>
              <Select value={config.provider} onValueChange={(v) => setConfig(prev => ({ ...prev, provider: v, model: '' }))}>
                <SelectTrigger data-testid="ocr-provider-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="disabled">Desactive</SelectItem>
                  <SelectItem value="openai">OpenAI (GPT-4o)</SelectItem>
                  <SelectItem value="gemini">Google Gemini</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {config.provider !== 'disabled' && (
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Modele</Label>
                <Select value={config.model || ''} onValueChange={(v) => setConfig(prev => ({ ...prev, model: v }))}>
                  <SelectTrigger data-testid="ocr-model-select"><SelectValue placeholder="Par defaut" /></SelectTrigger>
                  <SelectContent>
                    {(providerModels[config.provider] || []).map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {config.provider !== 'disabled' && (
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Clé API</Label>
              {config.apiKeyError && (
                <div className="flex items-start gap-2 p-3 mb-2 rounded-lg bg-red-50 border border-red-200 text-red-800" data-testid="ocr-key-error">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <div className="text-xs">
                    <p className="font-semibold">Clé API illisible</p>
                    <p className="mt-0.5">{config.apiKeyError}</p>
                  </div>
                </div>
              )}
              {config.apiKeyMasked && !newKey && config.apiKeyValid !== false && (
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                    <Check className="w-3 h-3 mr-1" /> Cle configuree: {config.apiKeyMasked}
                  </Badge>
                </div>
              )}
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Input
                    type={showKey ? 'text' : 'password'}
                    value={newKey}
                    onChange={(e) => setNewKey(e.target.value)}
                    placeholder={config.apiKeyMasked ? 'Coller une nouvelle cle pour remplacer...' : 'sk-... ou AIza...'}
                    className="pr-10"
                    data-testid="ocr-api-key"
                  />
                  <button onClick={() => setShowKey(!showKey)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button variant="outline" size="sm" disabled={testing || config.provider === 'disabled'} onClick={handleTest} data-testid="ocr-test-btn">
                  {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <TestTube className="w-4 h-4" />}
                  <span className="ml-1.5">Tester</span>
                </Button>
              </div>
              {testResult && (
                <div className={`flex items-center gap-2 p-2 rounded-lg text-xs ${testResult.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                  {testResult.success ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                  {testResult.message}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quotas */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base">Quotas mensuels</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Limite mensuelle</Label>
              <Input
                type="number" min="1" max="10000"
                value={config.monthlyQuota}
                onChange={(e) => setConfig(prev => ({ ...prev, monthlyQuota: e.target.value }))}
                data-testid="ocr-quota"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Alerte a (%)</Label>
              <Input
                type="number" min="1" max="100"
                value={config.alertThreshold}
                onChange={(e) => setConfig(prev => ({ ...prev, alertThreshold: e.target.value }))}
                data-testid="ocr-alert"
              />
            </div>
          </div>

          {/* Usage bar */}
          <div className="rounded-lg border p-3 space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500">Utilisation ce mois ({usage.month})</span>
              <span className="font-bold">{usage.total} / {usage.quota}</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${usagePercent >= 90 ? 'bg-red-500' : usagePercent >= 70 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                style={{ width: `${Math.min(usagePercent, 100)}%` }}
              />
            </div>
            {Object.keys(usage.byType || {}).length > 0 && (
              <div className="flex gap-3 text-[10px] text-gray-400">
                {Object.entries(usage.byType).map(([type, count]) => (
                  <span key={type}>{type.replace('_', ' ')}: {count}</span>
                ))}
              </div>
            )}
            {usagePercent >= (config.alertThreshold || 80) && (
              <div className="flex items-center gap-1.5 text-amber-600 text-xs">
                <AlertTriangle className="w-3.5 h-3.5" /> Seuil d'alerte atteint
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Enabled doc types */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base">Documents actifs</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {[
              { id: 'carte_grise', label: 'Carte Grise' },
              { id: 'permis_conduire', label: 'Permis de conduire' },
              { id: 'piece_identite', label: "Piece d'identite" }
            ].map(dt => {
              const active = config.enabledDocTypes.includes(dt.id);
              return (
                <button
                  key={dt.id}
                  onClick={() => toggleDocType(dt.id)}
                  className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${active ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-gray-50 border-gray-200 text-gray-400'}`}
                  data-testid={`ocr-doctype-${dt.id}`}
                >
                  {active ? <Check className="w-3.5 h-3.5 inline mr-1.5" /> : null}
                  {dt.label}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Save */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="bg-[#1db584] hover:bg-[#16a075]" data-testid="ocr-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Sauvegarder
        </Button>
      </div>
    </div>
  );
};

export default OCRSettings;
